/**
 * Le contenu d'un onglet de panneau : l'icône du panneau, la photo du fan, de la créatrice ou de
 * l'organisation, le nom, le compte de non lus. UNE copie, pour la bande d'onglets du bureau
 * (`TabStrip`) et celle du téléphone (`MobileShell`) — `TabStrip` les réexporte, d'autres écrans
 * (`panels/fan/devPosteMain`) les lisent par là.
 *
 * Photos rondes (le nouveau thème : « arrondi, jamais cubique »), nom en texte plein, non lus en
 * `Badge` à la marque — la marque sur ce qui t'attend. Rien ne dessine un carré à la main.
 */
import { useQuery } from "convex/react";
import { useAtomValue } from "jotai";

import { api } from "@/../convex/_generated/api";
import type { Id } from "@/../convex/_generated/dataModel";
import { Avatar, Badge } from "@/shared/ui";
import { conversationByIdAtom } from "@/panels/conversations/atoms";
import type { MockConversation } from "@/panels/conversations/mockData";
import { useUnreadCount } from "@/panels/conversations/unread";
import { useCreatorsList } from "@/panels/creators";
import type { Creator } from "@/panels/creators/mockData";
import { PANEL_VISUALS } from "@/shell/PanelThumbnails";

/* ============================================================ */
/* Fan : photo + nom + pastille de non lus                        */
/* ============================================================ */

/** Onglet d'une conversation Fan : photo ronde, nom, compte de non lus. Avant que la liste de
 *  conversations ne soit hydratée, le titre seul. */
export function FanTabContent({
  conversationId,
  title,
  grand = false,
}: {
  conversationId: string;
  title: string;
  /** 🎯 La forme du poste de chatteur : texte et photo plus grands, pastille lisible. */
  grand?: boolean;
}) {
  // Sélecteur par id : cet onglet ne re-rend que quand SA conversation change, pas à chaque
  // heartbeat de la liste (cf `conversationByIdAtom`).
  const conv = useAtomValue(conversationByIdAtom(conversationId));
  if (!conv) return <>{title}</>;
  return <OngletFan conv={conv} title={title} grand={grand} />;
}

/** Le contenu d'un onglet fan, prop-driven — ce que le harnais monte. */
export function OngletFan({ conv, title, grand = false }: { conv: MockConversation; title: string; grand?: boolean }) {
  const unread = useUnreadCount(conv);
  // Le nom VIVANT de la carte, pas le titre figé à l'ouverture : un onglet ouvert avant que la
  // carte arrive (« … ») prend son nom dès qu'elle est là.
  const nom = conv.fan_name || title;
  return (
    <span className={"flex min-w-0 items-center " + (grand ? "gap-2.5" : "gap-2")}>
      <MiniAvatar src={conv.fan_avatar_url ?? null} seed={conv.id} taille={grand ? 28 : 22} />
      <span className="truncate">{nom}</span>
      {unread > 0 && (
        <span aria-label={`${unread} unread`} className="flex shrink-0">
          <Badge ton="marque" size={grand ? "md" : "sm"}>
            {unread > 99 ? "99+" : unread}
          </Badge>
        </span>
      )}
    </span>
  );
}

/* ============================================================ */
/* Générique : l'icône du panneau + son titre                     */
/* ============================================================ */

/** Rendu générique d'un onglet : l'icône de `PANEL_VISUALS` + le titre. Sans entrée dans
 *  `PANEL_VISUALS`, le titre seul (pas d'icône de secours). */
export function GenericTabContent({ panelId, title }: { panelId: string; title: string }) {
  const visual = PANEL_VISUALS[panelId as keyof typeof PANEL_VISUALS];
  const Icon = visual?.icon;
  if (!Icon) return <>{title}</>;
  // 🪶 L'icône Phosphor, pas la tuile PNG de l'app (`AppGlyph`) : à côté de « Infos fan » et
  // « Francis » qui ont une vraie icône, l'image verte de Conversations « n'est pas dans le même
  // style » (Kayla, 12-09-2026).
  return (
    <span className="flex items-center gap-2">
      <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
      <span className="truncate">{title}</span>
    </span>
  );
}

/* ============================================================ */
/* Créatrice : pastille de couleur + photo + nom                  */
/* ============================================================ */

/** Onglet d'un creator-detail : couleur de la créatrice, photo, nom — lus en direct dans
 *  `useCreatorsList()` pour suivre un renommage ou une photo changée. Si le slug ne correspond plus
 *  à personne (archivée), le titre figé à l'ouverture. */
export function CreatorTabContent({ creatorSlug, fallbackTitle }: { creatorSlug: string; fallbackTitle: string }) {
  const creators = useCreatorsList();
  const creator = creators.find((c) => c.slug === creatorSlug);
  if (!creator) return <>{fallbackTitle}</>;
  return <CreatorTabInner creator={creator} />;
}

function CreatorTabInner({ creator }: { creator: Creator }) {
  return (
    <span className="flex items-center gap-2">
      <span aria-hidden className="regie-round h-2 w-2 shrink-0" style={{ background: creator.color }} />
      <MiniAvatar src={creator.avatar_url ?? null} seed={creator.id} />
      <span className="truncate">{creator.name}</span>
    </span>
  );
}

/* ============================================================ */
/* Organisation : logo + nom                                      */
/* ============================================================ */

/** Onglet d'une organisation : logo + nom. La query Convex est partagée avec les autres
 *  composants qui listent les orgs (Convex dédoublonne par arguments). Si l'accountId n'est pas
 *  dans la liste de l'appelant (un layout partagé par URL), le titre figé à l'ouverture. */
export function OrganisationTabContent({ accountId, fallbackTitle }: { accountId: Id<"accounts">; fallbackTitle: string }) {
  const orgs = useQuery(api.accounts.listMineForOrganisationSelector);
  const org = orgs?.find((o) => o.accountId === accountId);
  if (!org) return <>{fallbackTitle}</>;
  return (
    <span className="flex items-center gap-2">
      <OrganisationLogo logoUrl={org.logoUrl ?? null} name={org.name} />
      <span className="truncate">{org.name}</span>
    </span>
  );
}

/** Le logo rond d'une organisation ; sans logo, ses initiales (l'`Avatar` de la librairie). */
function OrganisationLogo({ logoUrl, name }: { logoUrl: string | null; name: string }) {
  return <Avatar src={logoUrl} nom={name} taille={22} />;
}

/** La photo ronde d'un fan ou d'une créatrice ; sans photo, l'avatar par défaut tiré de `seed`. */
export function MiniAvatar({
  src,
  seed,
  taille = 22,
}: {
  src: string | null;
  seed: string;
  /** Côté en px — 22 par défaut, 28 au poste de chatteur. */
  taille?: number;
}) {
  return <Avatar src={src} graine={seed} taille={taille} />;
}
